import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';

const API = process.env.REACT_APP_BACKEND_URL;
const COLORS = { primary: '#2b3380', success: '#4caf50', warning: '#ff9800', error: '#f44336' };

function DocumentDrive({ user, onLogout }) {
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchFiles();
    fetchFolders();
    fetchStats();
  }, [currentFolder]);

  const fetchFiles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/drive/files${currentFolder ? `?folder_id=${currentFolder}` : ''}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFiles(response.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching files:', error);
      setLoading(false);
    }
  };

  const fetchFolders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/drive/folders${currentFolder ? `?parent_id=${currentFolder}` : ''}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFolders(response.data || []);
    } catch (error) {
      console.error('Error fetching folders:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/drive/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/drive/folders`,
        { name: newFolderName, parent_id: currentFolder },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewFolderName('');
      setShowCreateFolder(false);
      fetchFolders();
    } catch (error) {
      console.error('Error creating folder:', error);
      alert(error.response?.data?.detail || 'Failed to create folder');
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    if (currentFolder) {
      formData.append('folder_id', currentFolder);
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/drive/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      fetchFiles();
      fetchStats();
      setUploading(false);
    } catch (error) {
      console.error('Error uploading file:', error);
      alert(error.response?.data?.detail || 'Failed to upload file');
      setUploading(false);
    }
  };

  const handleDownload = async (fileId, fileName) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/drive/files/${fileId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Failed to download file');
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (window.confirm('Delete this file?')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API}/api/drive/files/${fileId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchFiles();
        fetchStats();
      } catch (error) {
        console.error('Error deleting file:', error);
        alert('Failed to delete file');
      }
    }
  };

  const handleDeleteFolder = async (folderId) => {
    if (window.confirm('Delete this folder? (Must be empty)')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API}/api/drive/folders/${folderId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchFolders();
      } catch (error) {
        console.error('Error deleting folder:', error);
        alert(error.response?.data?.detail || 'Failed to delete folder');
      }
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
  };

  const getFileIcon = (extension) => {
    const icons = {
      '.pdf': '📄',
      '.doc': '📃',
      '.docx': '📃',
      '.xls': '📈',
      '.xlsx': '📈',
      '.ppt': '📊',
      '.pptx': '📊',
      '.jpg': '🖼️',
      '.jpeg': '🖼️',
      '.png': '🖼️',
      '.gif': '🖼️',
      '.zip': '🗄️',
      '.rar': '🗄️',
      '.txt': '📄',
    };
    return icons[extension.toLowerCase()] || '📄';
  };

  const storagePercent = stats.storage_used && stats.storage_limit
    ? (stats.storage_used / stats.storage_limit) * 100
    : 0;

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="h-screen flex flex-col bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow-md p-4">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-3xl font-bold" style={{ color: COLORS.primary }}>
              💾 My Drive
            </h1>
            <div className="flex gap-3">
              <button
                onClick={() => setShowCreateFolder(true)}
                className="px-4 py-2 bg-white border-2 rounded-lg font-medium hover:bg-gray-50"
                style={{ borderColor: COLORS.primary, color: COLORS.primary }}
              >
                📁 New Folder
              </button>
              <label className="px-4 py-2 text-white rounded-lg font-medium cursor-pointer hover:opacity-90"
                style={{ backgroundColor: COLORS.primary }}>
                ⬆️ Upload File
                <input
                  type="file"
                  onChange={handleUpload}
                  className="hidden"
                  disabled={uploading}
                />
              </label>
            </div>
          </div>

          {/* Storage Bar */}
          <div className="mt-4">
            <div className="flex justify-between text-sm text-gray-600 mb-1">
              <span>Storage: {formatFileSize(stats.storage_used || 0)} / {formatFileSize(stats.storage_limit || 0)}</span>
              <span>{storagePercent.toFixed(1)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="h-2 rounded-full transition-all"
                style={{
                  width: `${storagePercent}%`,
                  backgroundColor: storagePercent > 80 ? COLORS.error : COLORS.success
                }}
              />
            </div>
          </div>
        </div>

        {/* Breadcrumb */}
        {currentFolder && (
          <div className="bg-white border-b border-gray-200 px-4 py-2">
            <button
              onClick={() => setCurrentFolder(null)}
              className="text-blue-600 hover:underline"
            >
              ← Back to Home
            </button>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {uploading && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-blue-700">Uploading file...</p>
            </div>
          )}

          {loading ? (
            <div className="text-center py-20">
              <div className="text-4xl mb-4">⏳</div>
              <p className="text-gray-600">Loading...</p>
            </div>
          ) : (
            <div>
              {/* Folders */}
              {folders.length > 0 && (
                <div className="mb-8">
                  <h2 className="text-lg font-semibold text-gray-700 mb-3">Folders</h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {folders.map((folder) => (
                      <div
                        key={folder.id}
                        className="bg-white border-2 border-gray-200 rounded-lg p-4 hover:shadow-md cursor-pointer transition group"
                      >
                        <div onClick={() => setCurrentFolder(folder.id)}>
                          <div className="text-5xl mb-2">📁</div>
                          <p className="font-medium text-gray-800 truncate">{folder.name}</p>
                        </div>
                        <button
                          onClick={() => handleDeleteFolder(folder.id)}
                          className="mt-2 text-xs text-red-500 opacity-0 group-hover:opacity-100 transition"
                        >
                          Delete
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Files */}
              {files.length > 0 ? (
                <div>
                  <h2 className="text-lg font-semibold text-gray-700 mb-3">Files</h2>
                  <div className="bg-white rounded-lg shadow">
                    {files.map((file) => (
                      <div
                        key={file.id}
                        className="border-b border-gray-200 p-4 hover:bg-gray-50 flex items-center justify-between"
                      >
                        <div className="flex items-center gap-4 flex-1">
                          <span className="text-4xl">{getFileIcon(file.extension)}</span>
                          <div className="flex-1">
                            <p className="font-medium text-gray-800">{file.original_name}</p>
                            <p className="text-sm text-gray-600">
                              {formatFileSize(file.file_size)} • {new Date(file.uploaded_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleDownload(file.id, file.original_name)}
                            className="px-3 py-1 text-white rounded hover:opacity-90"
                            style={{ backgroundColor: COLORS.primary }}
                          >
                            Download
                          </button>
                          <button
                            onClick={() => handleDeleteFile(file.id)}
                            className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : folders.length === 0 ? (
                <div className="text-center py-20">
                  <div className="text-6xl mb-4">📂</div>
                  <p className="text-xl text-gray-600">Your drive is empty</p>
                  <p className="text-gray-500 mt-2">Upload files or create folders to get started</p>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>

      {/* Create Folder Modal */}
      {showCreateFolder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h2 className="text-xl font-bold mb-4" style={{ color: COLORS.primary }}>Create New Folder</h2>
            <input
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Folder name"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-4"
              onKeyPress={(e) => e.key === 'Enter' && handleCreateFolder()}
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setShowCreateFolder(false); setNewFolderName(''); }}
                className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateFolder}
                className="px-4 py-2 text-white rounded-lg"
                style={{ backgroundColor: COLORS.primary }}
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default DocumentDrive;
